import { Stack, Text } from '@mantine/core'
import { modals } from '@mantine/modals'

import { MODAL_OVER_MODAL_Z_INDEX } from '@/configs/componentDefaults'
import type { BulkUploadStore } from '@/services/state/BulkUploadStore'

import {
  bulkJobCopy,
  exitBodyUnknownMode,
  exitTitle,
  removeSubline,
  removeTitle,
  stillWaiting,
} from './bulkUploadJobCopy'

// Opened from the banner as well as the panel, so always stacked above a modal.
const confirmDefaults = { centered: true, zIndex: MODAL_OVER_MODAL_Z_INDEX, groupProps: { mt: 'xl' } } as const

function body(text: string, waiting: number) {
  return (
    <Stack gap="xs">
      <Text size="sm">{text}</Text>
      <Text size="sm">{stillWaiting(waiting)}</Text>
    </Stack>
  )
}

export function confirmStop(store: BulkUploadStore) {
  const job = store.job
  if (!job) return
  const copy = bulkJobCopy[job.mode]
  modals.openConfirmModal({
    ...confirmDefaults,
    title: copy.stop.title,
    children: body(copy.stop.body, job.waiting),
    labels: { confirm: 'Stop Upload', cancel: 'Keep Uploading' },
    confirmProps: { color: 'red' },
    onConfirm: () => store.stop(),
  })
}

/** Decision 16: only a file with progress to lose asks first. */
export function requestRemove(store: BulkUploadStore, name: string) {
  const job = store.job
  const row = job?.files.find(f => f.name === name)
  if (!job || !row) return
  if (row.status !== 'uploading' || row.progress === 0) {
    store.cancelFile(name)
    return
  }
  modals.openConfirmModal({
    ...confirmDefaults,
    title: removeTitle,
    children: (
      <Stack gap="xs">
        <Text size="sm">
          <Text span inherit fw={600}>
            {row.title}
          </Text>
          {bulkJobCopy[job.mode].removeBody(row.progress)}
        </Text>
        <Text size="sm" c="dimmed">
          {removeSubline}
        </Text>
      </Stack>
    ),
    labels: { confirm: 'Remove File', cancel: 'Keep It' },
    confirmProps: { color: 'red' },
    onConfirm: () => store.cancelFile(name),
  })
}

/**
 * Decision 50's warning before anything that ends the session on purpose. The caller runs
 * `stopForExit` on a yes, under its own loading overlay, since removals take a moment.
 */
export function confirmExitDuringUpload(store: BulkUploadStore): Promise<boolean> {
  if (!store.isAnyJobBusy) return Promise.resolve(true)
  return new Promise(resolve => {
    modals.openConfirmModal({
      ...confirmDefaults,
      title: exitTitle,
      children: <Text size="sm">{store.busyMode ? bulkJobCopy[store.busyMode].exitBody : exitBodyUnknownMode}</Text>,
      labels: { confirm: 'Stop Upload and Log Out', cancel: 'Keep Uploading' },
      confirmProps: { color: 'red' },
      onConfirm: () => resolve(true),
      onCancel: () => resolve(false),
      onClose: () => resolve(false),
    })
  })
}
